import { isEmpty } from 'lodash';
import { useEffect, useRef } from 'react';
import { createShortcutListener, findReservedKeyCollisions } from './helpers';
import type { KeyboardShortcut, KeyboardShortcutsOptions } from './types';

/**
 * Binds a set of keyboard shortcuts for as long as the calling component is
 * mounted.
 *
 * Shortcuts are ignored while the user is typing, while a key auto-repeats, and
 * whenever `options.enabled` is false.
 * Give each shortcut its own `enabled` flag to mirror the disabled state of the
 * button it stands in for.
 *
 * @param shortcuts - Bindings to apply. Safe to rebuild on every render.
 * @param options - Options applying to the whole set.
 */
export const useKeyboardShortcuts = (
  shortcuts: KeyboardShortcut[],
  options: KeyboardShortcutsOptions = {},
): void => {
  const shortcutsRef = useRef(shortcuts);
  const optionsRef = useRef(options);

  useEffect(() => {
    shortcutsRef.current = shortcuts;
    optionsRef.current = options;
  });

  useEffect(() => {
    const listener = createShortcutListener(
      () => shortcutsRef.current,
      () => optionsRef.current,
    );

    window.addEventListener('keydown', listener, true);

    return () => {
      window.removeEventListener('keydown', listener, true);
    };
  }, []);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    const collisions = findReservedKeyCollisions(shortcutsRef.current);

    if (!isEmpty(collisions)) {
      console.warn(
        `useKeyboardShortcuts: ${collisions.join(', ')} already control the 3D map. ` +
          'Pick another key, or require a modifier.',
      );
    }
  }, []);
};
